import { RESPONSE_TYPES } from "@root/constants";
import { HttpService } from "@services/http";
import {
  prepareErrorResponse,
  prepareResponseObject,
} from "@services/http/response";
import {
  PDF_PREVIEW,
  PDF_URL,
  USER_CHAT_GET_GOVT_RULES_ENDPOINT,
  USER_CHAT_GET_IMAGES_DESC_ENDPOINT,
  USER_CHAT_GET_REAL_ACC_ENDPOINT,
  USER_CHAT_QUERY_ENDPOINT,
  USER_CHAT_RISKCHECKER_ENDPOINT,
} from "./endpoints";
import { AxiosResponse } from "axios";

export class ChatService extends HttpService {
  sendInput = async (
    baseAuthUrl: string,
    data: Record<string, TObject>,
    withPdf: boolean = false,
    TID?: number
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = withPdf
        ? `${baseAuthUrl}/api/user/chat?project_id=${TID}`
        : `${baseAuthUrl}/${USER_CHAT_QUERY_ENDPOINT}`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  urlType = async (
    baseAuthUrl: string,
    data: Record<string, TObject>
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/api/url-type`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  riskCheckerCall = async (
    baseAuthUrl: string,
    data: any
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/${USER_CHAT_RISKCHECKER_ENDPOINT}`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };
  getImagesDesc = async (baseAuthUrl: string, data: any): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/${USER_CHAT_GET_IMAGES_DESC_ENDPOINT}`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };
  get_govt_rules = async (baseAuthUrl: string, data: any): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/${USER_CHAT_GET_GOVT_RULES_ENDPOINT}`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };
  get_real_accidents = async (
    baseAuthUrl: string,
    data: any
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/${USER_CHAT_GET_REAL_ACC_ENDPOINT}`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  sendReport = async (
    baseAuthUrl: string,
    data: Record<string, TObject>
  ): Promise<TObject> => {
    try {
      if (data) {
        const apiResponse = await this.post(
          `${baseAuthUrl}/api/feedback`,
          data,
          { headers: { removeLanguage: true } as any }
        );
        return apiResponse;
      }
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getThreads = async (
    baseAuthUrl: string,
    payload: any
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const apiResponse = await this.get(`${baseAuthUrl}/api/threads`, payload);

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  getThreadByID = async (
    baseAuthUrl: string,
    threadId: string,
    payload: any,
    pid: string | number
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/api/chat/${threadId}`,
        payload
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  getThreadByUID = async (
    baseAuthUrl: string,
    threadId: string,
    payload: any
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/api/chat-by-kuid/${threadId}`,
        payload
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  getRecentHistory = async (
    baseAuthUrl: string,
    threadId: string
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/api/thead/recent-chat?threadId=${threadId}`
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  getThreadByKUID = async (
    baseAuthUrl: string,
    threadId: string,
    payload: any
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/api/chat-by-kuid/${threadId}`,
        payload
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  deleteThreads = async (baseAuthUrl: string, data: any): Promise<TObject> => {
    try {
      const apiResponse = await this.delete(
        `${baseAuthUrl}/api/thread`,
        undefined,
        undefined,
        data
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  bulkDeleteThreads = async (baseAuthUrl: string): Promise<TObject> => {
    try {
      const apiResponse = await this.delete(`${baseAuthUrl}/api/chat-history`);

      return apiResponse;
    } catch (error) {
      return error;
    }
  };

  getUserProfile = async (
    baseUrl?: string
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const baseAuthUrl = process.env.REACT_APP_BASE_URL;
      const apiResponse = await this.get(`${baseAuthUrl}/api/user/profile`);
      return apiResponse;
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  getDownloadChat = async (
    baseAuthUrl: string,
    data: any
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/api/question-pdf`;
      const apiResponse = await this.post(url, data);
      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error;
    }
  };

  getPdfPreview = async (
    baseAuthUrl: string,
    payload: any
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/${PDF_PREVIEW}`,
        payload
      );
      return apiResponse;
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };
  isPdfUrlValid = async (baseAuthUrl: string, data: any): Promise<TObject> => {
    if (!data) {
      throw new Error("data payload is required.");
    }

    try {
      let url = `${baseAuthUrl}/${PDF_URL}`;
      const apiResponse = await this.post(url, data);
      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  getJournalistSubscriber = async (): Promise<TObject> => {
    try {
      const baseAuthUrl = process.env.REACT_APP_BASE_URL;
      let url = `${baseAuthUrl}/api/organization-membership/my-subscriber`;
      const apiResponse = await this.get(url);
      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };
}
